import { createEntityAdapter, createSelector, createSlice, Draft, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { PhotoPrintsPdpState } from '../store';

const adapter = createEntityAdapter<GroupUiState>();
const adapterSelectors = adapter.getSelectors<PhotoPrintsPdpState>(s => s.ui.groups);

export const { actions, reducer } = createSlice({
    name: 'groupUi',
    initialState: adapter.getInitialState(),
    reducers: {
        add: (state, action: PayloadAction<{ id: string }>) => {
            adapter.addOne(state, initialStateOf(action.payload.id));
        },

        addMany: (state, action: PayloadAction<{ ids: string[] }>) => {
            adapter.addMany(state, action.payload.ids.map(id => initialStateOf(id)));
        },

        increment: (
            state,
            action: PayloadAction<{ id: string; key: UploadCounters; incrementBy: number }>
        ) => {
            const { id, key, incrementBy } = action.payload;
            state.entities[id]![key] += incrementBy;
        },

        update: <TKey extends keyof WriteableGroupUiState>(
            state: Draft<EntityState<GroupUiState>>,
            action: PayloadAction<{ id: string; key: TKey; value: WriteableGroupUiState[TKey] }>
        ) => {
            const { id, key, value } = action.payload;
            state.entities[id] = {
                ...state.entities[id]!,
                [key]: value
            };
        },

        reset: <TKey extends keyof WriteableGroupUiState>(
            state: Draft<EntityState<GroupUiState>>,
            action: PayloadAction<{ id: string; key: TKey }>
        ) => {
            const { id, key } = action.payload;
            state.entities[id] = {
                ...state.entities[id]!,
                [key]: defaultValues[key]
            };
        }
    }
});

const defaultValues: WriteableGroupUiState = {
    successfulUploadsCount: 0,
    failedUploadsCount: 0,
    failedUploadsError: [],
    isNewGroup: false,
    hasVariantUpdated: false
};

export type WriteableGroupUiState = Omit<GroupUiState, 'id'>;
export type UploadCounters = 'failedUploadsCount' | 'successfulUploadsCount';
type GroupUiState = {
    id: string;
    failedUploadsCount: number;
    failedUploadsError: UploadError[];
    successfulUploadsCount: number;
    isNewGroup: boolean;
    hasVariantUpdated: boolean;
};

const initialStateOf = (id: string) => ({ id, ...defaultValues });

export const selectSuccessfulUploadsCount = createSelector([
    adapterSelectors.selectById
], groupUi => groupUi!.successfulUploadsCount);

export const selectFailedUploadsCount = createSelector([
    adapterSelectors.selectById
], groupUi => groupUi!.failedUploadsCount);

export const selectFailedUploadsError = createSelector([
    adapterSelectors.selectById
], groupUi => groupUi!.failedUploadsError);

export const selectHasVariantUpdated = createSelector([
    adapterSelectors.selectById
], groupUi => groupUi!.hasVariantUpdated);

export const selectIsNewGroup = createSelector([
    adapterSelectors.selectById
], groupUi => groupUi!.isNewGroup);